import React, { Component, ErrorInfo, ReactNode } from 'react';
import { appLogger, ErrorCategory } from '../../utils/errorLogger';
import { Box, Typography, Button } from '@mui/material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    appLogger.error(error, {
      metadata: { componentStack: errorInfo?.componentStack, ...errorInfo },
      source: 'react-error-boundary',
      category: ErrorCategory.CLIENT,
      path: window.location.pathname
    });
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Default fallback UI
      return (
        <Box 
          sx={{ 
            p: 4, 
            m: 2, 
            textAlign: 'center', 
            border: '1px solid #f0f0f0',
            borderRadius: 2,
            backgroundColor: '#fafafa'
          }}
        >
          <Typography variant="h5" component="h2" gutterBottom>
            Something went wrong
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Our team has been notified. Please try again later.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={this.handleReset}
          >
            Try Again
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
